/**
 * @fileoverview The map of a letterbox city plan's mission areas, built to
 * stay fast at thousands of polygons:
 *
 *  - polygons are fetched for the current viewport (and filter) only, from
 *    cityPlan.missionBoundaries, debounced on pan / zoom;
 *  - each polygon is a memoised @react-google-maps/api <Polygon> and only
 *    those whose bounding box overlaps the (inflated) viewport are mounted,
 *    the way Components/MapWithToolbar draws distribution zones;
 *  - below MIN_POLYGON_ZOOM nothing is drawn but a count, because a city
 *    seen from orbit is thousands of unreadable slivers.
 *
 * Clicking a polygon toggles it in the page's selection; selected polygons
 * are drawn on top in the selection colour. Nothing here edits geometry -
 * that stays with DrawMap in the single-area editor.
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { GoogleMap, InfoWindow, Polygon } from '@react-google-maps/api'
import ScriptLoader from 'Components/ScriptLoader'

import { CONFIG } from 'Config/config'
import SubmitButton from 'Components/Form/SubmitButton'
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator'
import {
  bboxIntersectsViewport,
  centroidOf,
  getItemBBox,
  viewportOf,
  viewportToBBoxInput,
} from 'Utils/Map/bbox'
import { MISSION_STATUS_ENUM } from 'shared/constants'
import InlineLabel from 'Containers/LetterboxMissions/v2/InlineLabel'

export const MIN_POLYGON_ZOOM = 11
const DEFAULT_ZOOM = 13

const Wrapper = styled.div`
  position: relative;
  height: ${props => props.height}px;
  border: 1px solid #eceef0;
  border-radius: 4px;
  overflow: hidden;

  .toolbar {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 2;
    display: flex;
    gap: 8px;
    align-items: center;
    background: rgba(255, 255, 255, 0.95);
    padding: 6px 10px;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    font-size: 12px;
    color: #011533;
  }

  .toolbar label {
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    font-weight: normal;
  }

  .notice {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
    background: rgba(255, 255, 255, 0.95);
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 12px;
    color: #011533;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  }

  .loading {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
  }

  .hover-label {
    position: absolute;
    bottom: 12px;
    left: 12px;
    z-index: 2;
    background: rgba(1, 21, 51, 0.85);
    color: #ffffff;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    pointer-events: none;
  }

  .info {
    color: #011533;
    font-size: 13px;
    min-width: 180px;
  }

  .info .info-label {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 4px 0;
  }

  .info .info-meta {
    color: #5a6b82;
    margin: 0 0 8px 0;
  }

  .info .info-actions {
    display: flex;
    gap: 12px;
  }

  .info .info-actions button {
    background: none;
    border: 0;
    padding: 0;
    color: #1878cf;
    cursor: pointer;
    font-weight: 600;
  }
`

const statusLabel = item =>
  item.status === MISSION_STATUS_ENUM.DRAFT
    ? item.isReadyToPublish
      ? 'Ready to publish'
      : 'Draft'
    : item.status

const COLOURS = {
  selected: '#1878CF',
  ready: '#2E7D32',
  draft: '#7E8FA5',
  published: '#F5A623',
}

/**
 * How a polygon is drawn. Pure so it can be read at a glance.
 * @param {object} item A missionBoundaries node
 * @param {boolean} selected
 * @returns {object} google.maps.PolygonOptions
 */
export const styleFor = (item, selected) => {
  if (selected)
    return {
      strokeColor: COLOURS.selected,
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: COLOURS.selected,
      fillOpacity: 0.45,
      zIndex: 3,
      clickable: true,
    }
  const isDraft = item.status === MISSION_STATUS_ENUM.DRAFT
  const colour = !isDraft
    ? COLOURS.published
    : item.isReadyToPublish
    ? COLOURS.ready
    : item.boundaryColour || COLOURS.draft
  return {
    strokeColor: colour,
    strokeOpacity: 0.9,
    strokeWeight: 1,
    fillColor: colour,
    fillOpacity: 0.2,
    zIndex: 1,
    clickable: true,
  }
}

const toPath = boundary =>
  (boundary || []).map(point => ({ lat: point.latitude, lng: point.longitude }))

const polygonPropsAreEqual = (prev, next) =>
  prev.item === next.item && prev.selected === next.selected

const MemoMissionPolygon = memo(
  ({ item, selected, onToggle, onHover, onOpen }) => (
    <Polygon
      paths={item.__path || (item.__path = toPath(item.boundary))}
      options={styleFor(item, selected)}
      onClick={event => {
        onToggle(item)
        onOpen(item, event.latLng)
      }}
      onMouseOver={() => onHover(item)}
      onMouseOut={() => onHover(null)}
    />
  ),
  polygonPropsAreEqual,
)
MemoMissionPolygon.displayName = 'MemoMissionPolygon'

const MissionBoundariesMap = ({
  city,
  items,
  loading,
  totalCount,
  isSelected,
  onToggle,
  onRename,
  onRecolour,
  onEdit,
  onEditArea,
  onSelectVisible,
  onViewportChange,
  focusPoints,
  height,
  showPublished,
  onShowPublishedChange,
}) => {
  const mapRef = useRef(null)
  const rafRef = useRef(null)
  const [viewport, setViewport] = useState(null)
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  // The area under the pointer (label shown in the corner) and the one
  // clicked last (popup with its label and a way to rename it).
  const [hovered, setHovered] = useState(null)
  const [opened, setOpened] = useState(null)

  const onOpen = useCallback(
    (item, latLng) =>
      setOpened({
        item,
        position: { lat: latLng.lat(), lng: latLng.lng() },
      }),
    [],
  )
  const closeOpened = useCallback(() => setOpened(null), [])

  // A refresh replaces the items; keep the popup on the fresh copy of its
  // mission (renamed label) or drop it if the mission left the view.
  useEffect(() => {
    if (!opened) return
    const fresh = items.find(item => item.id === opened.item.id)
    if (!fresh) setOpened(null)
    else if (fresh !== opened.item) setOpened({ ...opened, item: fresh })
  }, [items]) // eslint-disable-line react-hooks/exhaustive-deps

  const center = useMemo(
    () =>
      city && city.latitude && city.longitude
        ? { lat: Number(city.latitude), lng: Number(city.longitude) }
        : { lat: 0, lng: 0 },
    [city],
  )

  const publishViewport = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const next = viewportOf(map)
    setViewport(next)
    setZoom(map.getZoom())
    if (onViewportChange)
      onViewportChange({ bbox: viewportToBBoxInput(next), zoom: map.getZoom() })
  }, [onViewportChange])

  const onBoundsChanged = useCallback(() => {
    if (rafRef.current) return
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null
      publishViewport()
    })
  }, [publishViewport])

  useEffect(
    () => () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current)
    },
    [],
  )

  // Fit the map to the points asked for (e.g. the selection) when they change.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !focusPoints || focusPoints.length === 0) return
    const bounds = new window.google.maps.LatLngBounds()
    focusPoints.forEach(point =>
      bounds.extend({ lat: point.latitude, lng: point.longitude }),
    )
    map.fitBounds(bounds, 40)
  }, [focusPoints])

  const drawPolygons = zoom >= MIN_POLYGON_ZOOM
  const visibleItems = useMemo(() => {
    if (!drawPolygons) return []
    const filtered = showPublished
      ? items
      : items.filter(item => item.status === MISSION_STATUS_ENUM.DRAFT)
    if (!viewport || filtered.length < 200) return filtered
    return filtered.filter(item =>
      bboxIntersectsViewport(getItemBBox(item), viewport),
    )
  }, [items, viewport, drawPolygons, showPublished])

  const selectVisible = useCallback(() => {
    if (onSelectVisible) onSelectVisible(visibleItems.map(item => item.id))
  }, [onSelectVisible, visibleItems])

  const fitToLoaded = useCallback(() => {
    const map = mapRef.current
    if (!map || items.length === 0) return
    const point = centroidOf(items.flatMap(item => item.boundary || []))
    if (point) map.panTo(point)
  }, [items])

  return (
    <Wrapper height={height}>
      <div className="toolbar">
        <SubmitButton
          value={`Select visible (${visibleItems.length})`}
          size="small"
          variant="outlined"
          disabled={visibleItems.length === 0}
          onClick={selectVisible}
        />
        <SubmitButton
          value="Centre on areas"
          size="small"
          variant="outlined"
          disabled={items.length === 0}
          onClick={fitToLoaded}
        />
        <label>
          <input
            type="checkbox"
            checked={showPublished}
            onChange={event => onShowPublishedChange(event.target.checked)}
          />
          Show published
        </label>
      </div>
      {loading && (
        <div className="loading">
          <LoadingIndicator />
        </div>
      )}
      {!drawPolygons && (
        <div className="notice">
          Zoom in to see the {totalCount.toLocaleString()} mission areas in view
        </div>
      )}
      <GoogleMap
        onLoad={map => {
          mapRef.current = map
          publishViewport()
        }}
        onBoundsChanged={onBoundsChanged}
        onIdle={publishViewport}
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={DEFAULT_ZOOM}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          gestureHandling: 'greedy',
          // Google's own place popups would steal clicks meant for areas.
          clickableIcons: false,
        }}
      >
        {visibleItems.map(item => (
          <MemoMissionPolygon
            key={item.id}
            item={item}
            selected={isSelected(item.id)}
            onToggle={onToggle}
            onHover={setHovered}
            onOpen={onOpen}
          />
        ))}
        {opened && (
          <InfoWindow position={opened.position} onCloseClick={closeOpened}>
            <div className="info">
              <p className="info-label">
                <InlineLabel
                  mission={opened.item}
                  onRename={onRename}
                  onRecolour={onRecolour}
                />
              </p>
              <p className="info-meta">
                {statusLabel(opened.item)}
                {opened.item.isoWeekKey
                  ? ` · W${opened.item.isoWeekKey.split('-')[1]}, ${
                      opened.item.isoWeekKey.split('-')[0]
                    }`
                  : ''}
              </p>
              {(onEdit || onEditArea) && (
                <div className="info-actions">
                  {onEdit && (
                    <button
                      type="button"
                      onClick={() => onEdit(opened.item)}
                      title="Rename, re-date or adjust the pay of this mission"
                    >
                      Edit
                    </button>
                  )}
                  {onEditArea && (
                    <button
                      type="button"
                      onClick={() => onEditArea(opened.item)}
                    >
                      Edit area
                    </button>
                  )}
                </div>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      {hovered && (!opened || hovered.id !== opened.item.id) && (
        <div className="hover-label">{hovered.label || hovered.id}</div>
      )}
    </Wrapper>
  )
}

MissionBoundariesMap.propTypes = {
  city: PropTypes.shape({
    latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  items: PropTypes.array,
  loading: PropTypes.bool,
  totalCount: PropTypes.number,
  isSelected: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onRename: PropTypes.func,
  onRecolour: PropTypes.func,
  onEdit: PropTypes.func,
  onEditArea: PropTypes.func,
  onSelectVisible: PropTypes.func,
  onViewportChange: PropTypes.func,
  focusPoints: PropTypes.array,
  height: PropTypes.number,
  showPublished: PropTypes.bool,
  onShowPublishedChange: PropTypes.func,
}

MissionBoundariesMap.defaultProps = {
  city: null,
  items: [],
  loading: false,
  totalCount: 0,
  onRename: null,
  onRecolour: null,
  onEdit: null,
  onEditArea: null,
  onSelectVisible: null,
  onViewportChange: null,
  focusPoints: null,
  height: 520,
  showPublished: true,
  onShowPublishedChange: () => {},
}

/**
 * The Maps JS is loaded the way the rest of the platform loads it (a single
 * script tag through ScriptLoader, which never injects twice) rather than
 * through useJsApiLoader: DrawMap (the area editor on this page) injects the
 * same script with the drawing library, and two loaders on one page clash.
 * The library set is the superset of what DrawMap and the meeting point
 * modal's PlaceAutocomplete need.
 */
export const MAPS_SCRIPT_SRC = `https://maps.googleapis.com/maps/api/js?key=${
  CONFIG.GOOGLE_API_KEY
}&libraries=drawing,visualization,geometry,places&v=3.64`

const MissionBoundariesMapLoader = props => (
  <ScriptLoader src={MAPS_SCRIPT_SRC}>
    <MissionBoundariesMap {...props} />
  </ScriptLoader>
)

export default MissionBoundariesMapLoader
